import { useState, useEffect } from 'react';
import axios from 'axios';
import IndustrySwitcher from './IndustrySwitcher';
import ChangePasswordModal from './ChangePasswordModal';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SettingsSection({ user, onUserUpdate }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    name: user.name,
    email: user.email,
    industry: user.industry
  });
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    postReminders: true,
    weeklyReport: false
  });
  const [brandSettings, setBrandSettings] = useState({
    brand_mode: user.brand_mode || 'manual',
    brand_name: user.brand_name || '',
    brand_primary_color: user.brand_primary_color || '#FF6B35',
    brand_secondary_color: user.brand_secondary_color || '#F7931E',
    brand_accent_color_1: user.brand_accent_color_1 || '',
    brand_accent_color_2: user.brand_accent_color_2 || '',
    
    // AI Generation Controls
    follow_brand_colors: user.follow_brand_colors !== undefined ? user.follow_brand_colors : true,
    color_intensity: user.color_intensity || 'medium',
    brand_name_in_scene: user.brand_name_in_scene !== undefined ? user.brand_name_in_scene : true,
    educational_overlay: user.educational_overlay !== undefined ? user.educational_overlay : true,
    brand_scene_mode: user.brand_scene_mode || 'text_only',  // NEW: text_only, logo_only, text_and_logo
    
    // Watermark Settings
    watermark_enabled: user.watermark_enabled !== undefined ? user.watermark_enabled : true,
    watermark_adaptive: user.watermark_adaptive !== undefined ? user.watermark_adaptive : true,
    watermark_position: user.watermark_position || 'bottom-right',
    watermark_size: user.watermark_size || 10
  });
  const [aiColors, setAiColors] = useState(null);
  const [generatingColors, setGeneratingColors] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [analyzingWebsite, setAnalyzingWebsite] = useState(false);
  const [websiteBrandData, setWebsiteBrandData] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const [brandSaveSuccess, setBrandSaveSuccess] = useState(false);
  
  // Theme settings
  const [themeSettings, setThemeSettings] = useState({
    theme_mode: user.theme_mode || 'dark',
    theme_accent_color: user.theme_accent_color || 'orange'
  });
  const [savingTheme, setSavingTheme] = useState(false);
  
  // Dual logo system
  const [logoLightFile, setLogoLightFile] = useState(null);
  const [logoDarkFile, setLogoDarkFile] = useState(null);
  const [logoLightPreview, setLogoLightPreview] = useState(user.brand_logo_light_bg ? `${BACKEND_URL}${user.brand_logo_light_bg}` : null);
  const [logoDarkPreview, setLogoDarkPreview] = useState(user.brand_logo_dark_bg ? `${BACKEND_URL}${user.brand_logo_dark_bg}` : null);
  const [uploadingLogoLight, setUploadingLogoLight] = useState(false);
  const [uploadingLogoDark, setUploadingLogoDark] = useState(false);
  
  // Scene logo system (NEW)
  const [logoSceneFile, setLogoSceneFile] = useState(null);
  const [logoScenePreview, setLogoScenePreview] = useState(user.brand_scene_logo ? `${BACKEND_URL}${user.brand_scene_logo}` : null);
  const [uploadingLogoScene, setUploadingLogoScene] = useState(false);
  
  // Change password modal
  const [showChangePassword, setShowChangePassword] = useState(false);


  const generateAIColors = async () => {
    setGeneratingColors(true);
    try {
      const response = await axios.post(`${API}/users/${user.id}/brand/generate-ai-colors`);
      setAiColors(response.data.colors);
      // Don't auto-apply, let user review first
    } catch (error) {
      alert('Failed to generate AI colors: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingColors(false);
    }
  };

  const applyAIColors = () => {
    if (aiColors) {
      setBrandSettings({
        ...brandSettings,
        brand_primary_color: aiColors.primary,
        brand_secondary_color: aiColors.secondary,
        brand_accent_color_1: aiColors.accent1,
        brand_accent_color_2: aiColors.accent2
      });
      setAiColors(null); // Clear after applying
    }
  };

  const analyzeWebsite = async () => {
    if (!websiteUrl.trim()) {
      alert('Please enter a website URL');
      return;
    }

    setAnalyzingWebsite(true);
    try {
      const response = await axios.post(`${API}/users/${user.id}/brand/analyze-website`, {
        url: websiteUrl
      });
      setWebsiteBrandData({
        ...response.data.brand_data,
        logo_extracted: response.data.logo_extracted,
        logo_url: response.data.logo_url
      });
      
      // If logo was extracted, refresh logo preview
      if (response.data.logo_extracted && response.data.logo_url) {
        setLogoLightPreview(`${BACKEND_URL}${response.data.logo_url}`);
      }
    } catch (error) {
      alert('Failed to analyze website: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzingWebsite(false);
    }
  };

  const applyWebsiteBrand = () => {
    if (websiteBrandData) {
      setBrandSettings({
        ...brandSettings,
        brand_name: websiteBrandData.brand_name || brandSettings.brand_name,
        brand_primary_color: websiteBrandData.primary,
        brand_secondary_color: websiteBrandData.secondary,
        brand_accent_color_1: websiteBrandData.accent1,
        brand_accent_color_2: websiteBrandData.accent2
      });
      setWebsiteBrandData(null); // Clear after applying
      setWebsiteUrl(''); // Clear URL
    }
  };


  const saveProfile = () => {
    axios.put(`${BACKEND_URL}/api/users/${user.id}`, profile)
      .then(() => {
        alert('✅ Profile updated successfully!');
        onUserUpdate({ ...user, ...profile });
      })
      .catch(err => alert('❌ Error: ' + (err.response?.data?.detail || err.message)));
  };

  const updateImageProvider = async (provider) => {
    try {
      await axios.put(`${BACKEND_URL}/api/users/${user.id}/brand/settings`, {
        preferred_image_provider: provider
      });
      alert(`✅ AI Provider switched to ${provider === 'fal' ? 'Fal.ai FLUX' : 'Gemini'}!`);
      onUserUpdate({ ...user, preferred_image_provider: provider });
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const saveNotifications = () => {
    alert('Notification preferences saved!');
  };

  const saveThemeSettings = async () => {
    try {
      setSavingTheme(true);
      
      await axios.put(`${API}/users/${user.id}/theme`, {
        theme_mode: themeSettings.theme_mode,
        theme_accent_color: themeSettings.theme_accent_color
      });

      // Update user object
      const updatedUser = {
        ...user,
        theme_mode: themeSettings.theme_mode,
        theme_accent_color: themeSettings.theme_accent_color
      };
      onUserUpdate(updatedUser);
      
      // Save to localStorage
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));

      // Apply theme mode immediately
      document.documentElement.classList.remove('dark', 'light');
      document.documentElement.classList.add(themeSettings.theme_mode);

      // Apply accent color immediately
      // Only purple uses gradient, others use solid AI-inspired soft colors
      const accentColors = {
        orange: { from: '#e07a5f', to: '#e07a5f', via: '#e07a5f', isGradient: false },
        blue: { from: '#5b8def', to: '#5b8def', via: '#5b8def', isGradient: false },
        purple: { from: '#6B46C1', to: '#A78BFA', via: '#8B5CF6', isGradient: true },
        green: { from: '#6dbf8b', to: '#6dbf8b', via: '#6dbf8b', isGradient: false },
        red: { from: '#e07a7a', to: '#e07a7a', via: '#e07a7a', isGradient: false },
        gold: { from: '#d4a853', to: '#d4a853', via: '#d4a853', isGradient: false }
      };
      
      const colors = accentColors[themeSettings.theme_accent_color] || accentColors.purple;
      document.documentElement.style.setProperty('--accent-from', colors.from);
      document.documentElement.style.setProperty('--accent-to', colors.to);
      document.documentElement.style.setProperty('--accent-via', colors.via);
      
      // Add/remove gradient class based on selected color
      if (colors.isGradient) {
        document.documentElement.classList.add('accent-uses-gradient');
        document.documentElement.classList.remove('accent-uses-solid');
      } else {
        document.documentElement.classList.remove('accent-uses-gradient');
        document.documentElement.classList.add('accent-uses-solid');
      }

      alert('✅ Theme settings saved successfully!');
    } catch (error) {
      console.error('Error saving theme:', error);
      alert('❌ Failed to save theme settings');
    } finally {
      setSavingTheme(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold gradient-text mb-6">Settings</h2>

      {/* Settings Tabs */}
      <div className="rounded-xl border mb-6" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <div className="flex space-x-1 p-2">
          {[
            { id: 'profile', label: 'Profile', icon: '👤' },
            { id: 'appearance', label: 'Appearance', icon: '🎨' },
            { id: 'brand', label: 'Brand', icon: '🏷️' },
            { id: 'industries', label: 'Industries', icon: '🏭' },
            { id: 'notifications', label: 'Notifications', icon: '🔔' },
            { id: 'preferences', label: 'Preferences', icon: '⚙️' },
            { id: 'security', label: 'Security', icon: '🔒' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'accent-gradient text-white'
                  : ''
              }`}
              style={activeTab !== tab.id ? {
                color: 'var(--text-muted)',
                backgroundColor: 'transparent'
              } : {}}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.color = 'var(--text-primary)';
                  e.target.style.backgroundColor = 'var(--hover-bg)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.color = 'var(--text-muted)';
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="theme-card border rounded-xl p-6">
          <h3 className="text-xl font-bold mb-6 accent-text">Profile Information</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Name</label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="theme-input w-full px-4 py-3 border rounded-lg focus:ring-2 outline-none"
                style={{ '--tw-ring-color': 'var(--accent-from)' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Email</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                className="theme-input w-full px-4 py-3 border rounded-lg focus:ring-2 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Industry</label>
              <select
                value={profile.industry}
                onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
                className="theme-input w-full px-4 py-3 border rounded-lg focus:ring-2 outline-none"
              >
                <option>Construction</option>
                <option>Medical</option>
                <option>Retail</option>
                <option>Technology</option>
                <option>Real Estate</option>
                <option>Finance</option>
                <option>Education</option>
                <option>Food & Restaurant</option>
                <option>Fashion & Beauty</option>
                <option>Travel & Hospitality</option>
                <option>Fitness & Wellness</option>
                <option>Legal Services</option>
                <option>E-commerce</option>
                <option>Automotive</option>
                <option>Entertainment</option>
              </select>
            </div>

            <button
              onClick={saveProfile}
              className="w-full px-6 py-3 accent-gradient text-white rounded-lg font-medium transition btn-glow"
            >
              💾 Save Changes
            </button>
          </div>

          {/* Security Section */}
          <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <h3 className="text-xl font-bold mb-4 accent-text">🔒 Security</h3>
            <button
              onClick={() => setShowChangePassword(true)}
              className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
            >
              🔑 Change Password
            </button>
          </div>

          {/* Admin-only AI Provider Toggle */}
          {user.role === 'admin' && (
            <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-xl font-bold mb-4 accent-text">🤖 AI Provider (Admin Only)</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                Choose which AI model to use for image generation
              </p>
              
              <div className="flex gap-4">
                <button
                  onClick={() => updateImageProvider('fal')}
                  className={`flex-1 px-6 py-4 rounded-lg font-medium transition ${
                    (user.preferred_image_provider || 'fal') === 'fal'
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  <div className="text-lg mb-1">⚡ Fal.ai FLUX</div>
                  <div className="text-xs opacity-80">Professional Quality</div>
                </button>
                
                <button
                  onClick={() => updateImageProvider('gemini')}
                  className={`flex-1 px-6 py-4 rounded-lg font-medium transition ${
                    user.preferred_image_provider === 'gemini'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  <div className="text-lg mb-1">🌟 Gemini</div>
                  <div className="text-xs opacity-80">Fast & Reliable</div>
                </button>
              </div>
              
              <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <strong>Current:</strong> {(user.preferred_image_provider || 'fal') === 'fal' ? 'Fal.ai FLUX 1.1 Ultra' : 'Google Gemini 2.5 Flash'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePasswordModal
          userId={user.id}
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => {
            setShowChangePassword(false);
            alert('Password changed successfully! Please use your new password next time you login.');
          }}
        />
      )}

      {/* Appearance Tab */}
      {activeTab === 'appearance' && (
        <div className="theme-card border rounded-xl p-6">
          <h3 className="text-xl font-bold mb-2 accent-text">🎨 Theme & Appearance</h3>
          <p className="mb-6" style={{ color: 'var(--text-muted)' }}>Customize the look and feel of your dashboard</p>
          
          <div className="space-y-8">
            {/* Theme Mode Section */}
            <div className="theme-card-nested rounded-xl p-6 border">
              <h4 className="font-semibold mb-4 flex items-center" style={{ color: 'var(--text-primary)' }}>
                <span className="mr-2">🌗</span>
                Theme Mode
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setThemeSettings({ ...themeSettings, theme_mode: 'dark' })}
                  className={`p-6 rounded-xl border-2 transition ${
                    themeSettings.theme_mode === 'dark'
                      ? 'accent-gradient border-transparent'
                      : ''
                  }`}
                  style={themeSettings.theme_mode !== 'dark' ? {
                    borderColor: 'var(--border-color)',
                    backgroundColor: 'var(--card-bg)'
                  } : {}}
                >
                  <div className="text-4xl mb-2">🌙</div>
                  <div className="font-medium" style={{ color: themeSettings.theme_mode === 'dark' ? '#ffffff' : 'var(--text-primary)' }}>Dark Mode</div>
                  <div className="text-sm mt-1" style={{ color: themeSettings.theme_mode === 'dark' ? '#e0e0e0' : 'var(--text-muted)' }}>Easy on the eyes</div>
                </button>
                
                <button
                  onClick={() => setThemeSettings({ ...themeSettings, theme_mode: 'light' })}
                  className={`p-6 rounded-xl border-2 transition ${
                    themeSettings.theme_mode === 'light'
                      ? 'accent-gradient border-transparent'
                      : ''
                  }`}
                  style={themeSettings.theme_mode !== 'light' ? {
                    borderColor: 'var(--border-color)',
                    backgroundColor: 'var(--card-bg)'
                  } : {}}
                >
                  <div className="text-4xl mb-2">☀️</div>
                  <div className="font-medium" style={{ color: themeSettings.theme_mode === 'light' ? '#ffffff' : 'var(--text-primary)' }}>Light Mode</div>
                  <div className="text-sm mt-1" style={{ color: themeSettings.theme_mode === 'light' ? '#e0e0e0' : 'var(--text-muted)' }}>Bright and clean</div>
                </button>
              </div>
            </div>

            {/* Accent Color Section */}
            <div className="theme-card-nested rounded-xl p-6 border">
              <h4 className="font-semibold mb-4 flex items-center" style={{ color: 'var(--text-primary)' }}>
                <span className="mr-2">🎨</span>
                Accent Color
              </h4>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { id: 'orange', name: 'Sunset', icon: '🟠', color: '#e07a5f', isGradient: false },
                  { id: 'blue', name: 'Ocean', icon: '🔵', color: '#5b8def', isGradient: false },
                  { id: 'purple', name: 'Neural', icon: '🟣', colors: 'from-purple-600 via-violet-500 to-purple-400', isGradient: true },
                  { id: 'green', name: 'Mint', icon: '🟢', color: '#6dbf8b', isGradient: false },
                  { id: 'red', name: 'Rose', icon: '🔴', color: '#e07a7a', isGradient: false },
                  { id: 'gold', name: 'Amber', icon: '🟡', color: '#d4a853', isGradient: false }
                ].map((colorOption) => (
                  <button
                    key={colorOption.id}
                    onClick={() => setThemeSettings({ ...themeSettings, theme_accent_color: colorOption.id })}
                    className={`p-4 rounded-xl border-2 transition ${
                      themeSettings.theme_accent_color === colorOption.id
                        ? ''
                        : ''
                    }`}
                    style={themeSettings.theme_accent_color === colorOption.id ? {
                      borderColor: 'var(--text-primary)',
                      backgroundColor: 'var(--secondary-bg)'
                    } : {
                      borderColor: 'var(--border-color)',
                      backgroundColor: 'var(--card-bg)'
                    }}
                  >
                    <div className="text-3xl mb-2">{colorOption.icon}</div>
                    <div 
                      className={`h-8 rounded mb-2 ${colorOption.isGradient ? `bg-gradient-to-r ${colorOption.colors}` : ''}`}
                      style={!colorOption.isGradient ? { backgroundColor: colorOption.color } : {}}
                    ></div>
                    <div className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{colorOption.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview Section */}
            <div className="theme-card-nested rounded-xl p-6 border">
              <h4 className="font-semibold mb-4 flex items-center" style={{ color: 'var(--text-primary)' }}>
                <span className="mr-2">👁️</span>
                Preview
              </h4>
              <div className="p-6 rounded-xl" style={{ 
                backgroundColor: themeSettings.theme_mode === 'dark' ? '#1a1a1a' : '#f5f5f5' 
              }}>
                <h5 className="text-lg font-bold mb-2" style={{ 
                  color: themeSettings.theme_mode === 'dark' ? '#ffffff' : '#1a1a1a' 
                }}>
                  Sample Dashboard Card
                </h5>
                <p className="mb-4" style={{ 
                  color: themeSettings.theme_mode === 'dark' ? '#b0b0b0' : '#4a4a4a' 
                }}>
                  This is how your content will look with the selected theme.
                </p>
                <button
                  className={`px-6 py-2 text-white rounded-lg font-medium ${
                    themeSettings.theme_accent_color === 'purple' ? 'bg-gradient-to-r from-purple-600 via-violet-500 to-purple-400' : ''
                  }`}
                  style={themeSettings.theme_accent_color !== 'purple' ? {
                    backgroundColor: 
                      themeSettings.theme_accent_color === 'orange' ? '#e07a5f' :
                      themeSettings.theme_accent_color === 'blue' ? '#5b8def' :
                      themeSettings.theme_accent_color === 'green' ? '#6dbf8b' :
                      themeSettings.theme_accent_color === 'red' ? '#e07a7a' :
                      themeSettings.theme_accent_color === 'gold' ? '#d4a853' : '#e07a5f'
                  } : {}}
                >
                  Sample Button
                </button>
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={saveThemeSettings}
              disabled={savingTheme}
              className="w-full px-6 py-3 accent-gradient text-white rounded-lg font-medium transition disabled:opacity-50"
            >
              {savingTheme ? '⏳ Saving...' : '💾 Save Theme Settings'}
            </button>
          </div>
        </div>
      )}

      {/* Brand Tab */}
      {activeTab === 'brand' && (
        <div className="theme-card border rounded-xl p-6">
          <h3 className="text-xl font-bold mb-2 accent-text">Brand Customization</h3>
          <p className="mb-6" style={{ color: 'var(--text-muted)' }}>Upload your logo and set brand colors for AI-generated content</p>
          
          <div className="space-y-6">
            {/* Brand Mode Selector */}
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/50 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-3">🎨 Brand Option</h4>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    setBrandSettings({ ...brandSettings, brand_mode: 'manual' });
                    setAiColors(null);
                  }}
                  className={`p-4 rounded-lg border-2 transition ${
                    brandSettings.brand_mode === 'manual'
                      ? 'border-orange-500 bg-orange-500/20'
                      : 'border-gray-600 bg-gray-800 hover:border-orange-500/50'
                  }`}
                >
                  <div className="text-3xl mb-2">🎨</div>
                  <div className="text-white font-semibold">Manual Brand</div>
                  <div className="text-xs text-gray-400 mt-1">Choose your own colors</div>
                </button>
                <button
                  onClick={() => {
                    setBrandSettings({ ...brandSettings, brand_mode: 'openai' });
                  }}
                  className={`p-4 rounded-lg border-2 transition ${
                    brandSettings.brand_mode === 'openai'
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-gray-600 bg-gray-800 hover:border-blue-500/50'
                  }`}
                >
                  <div className="text-3xl mb-2">🤖</div>
                  <div className="text-white font-semibold">OpenAI Brand</div>
                  <div className="text-xs text-gray-400 mt-1">AI-powered colors</div>
                </button>
              </div>

              {/* AI Color Generator */}
              {brandSettings.brand_mode === 'openai' && (
                <div className="mt-4 space-y-3">
                  <button
                    onClick={generateAIColors}
                    disabled={generatingColors}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition disabled:opacity-50"
                  >
                    {generatingColors ? '✨ Generating AI Colors...' : '✨ Generate AI Brand Colors'}
                  </button>

                  {aiColors && (
                    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                      <div className="text-white font-semibold mb-3">🎨 AI Generated Colors</div>
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        <div className="text-center">
                          <div 
                            className="h-16 rounded-lg border-2 border-gray-600 mb-1"
                            style={{ backgroundColor: aiColors.primary }}
                          ></div>
                          <div className="text-xs text-gray-400">Primary</div>
                          <div className="text-xs text-gray-300 font-mono">{aiColors.primary}</div>
                        </div>
                        <div className="text-center">
                          <div 
                            className="h-16 rounded-lg border-2 border-gray-600 mb-1"
                            style={{ backgroundColor: aiColors.secondary }}
                          ></div>
                          <div className="text-xs text-gray-400">Secondary</div>
                          <div className="text-xs text-gray-300 font-mono">{aiColors.secondary}</div>
                        </div>
                        <div className="text-center">
                          <div 
                            className="h-16 rounded-lg border-2 border-gray-600 mb-1"
                            style={{ backgroundColor: aiColors.accent1 }}
                          ></div>
                          <div className="text-xs text-gray-400">Accent 1</div>
                          <div className="text-xs text-gray-300 font-mono">{aiColors.accent1}</div>
                        </div>
                        <div className="text-center">
                          <div 
                            className="h-16 rounded-lg border-2 border-gray-600 mb-1"
                            style={{ backgroundColor: aiColors.accent2 }}
                          ></div>
                          <div className="text-xs text-gray-400">Accent 2</div>
                          <div className="text-xs text-gray-300 font-mono">{aiColors.accent2}</div>
                        </div>
                      </div>
                      <button
                        onClick={applyAIColors}
                        className="w-full px-4 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition"
                      >
                        ✅ Apply These Colors
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Website Brand Analyzer */}
            <div className="bg-gradient-to-r from-green-900/30 to-teal-900/30 border border-green-500/50 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-3">🌐 Brand Recognition from Website</h4>
              <p className="text-gray-400 text-sm mb-3">
                Paste your website URL and we'll automatically extract your brand colors and name
              </p>
              
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  />
                  <button
                    onClick={analyzeWebsite}
                    disabled={analyzingWebsite || !websiteUrl.trim()}
                    className="px-6 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg font-semibold hover:from-green-600 hover:to-teal-600 transition disabled:opacity-50"
                  >
                    {analyzingWebsite ? '🔍 Analyzing...' : '🔍 Analyze'}
                  </button>
                </div>

                {websiteBrandData && (
                  <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                    <div className="text-white font-semibold mb-2">
                      🎨 Extracted Brand Data
                    </div>
                    
                    {/* Brand Name */}
                    {websiteBrandData.brand_name && (
                      <div className="mb-3 p-2 bg-gray-700/50 rounded">
                        <div className="text-xs text-gray-400">Brand Name</div>
                        <div className="text-white font-semibold">{websiteBrandData.brand_name}</div>
                      </div>
                    )}

                    {/* Logo Status */}
                    {websiteBrandData.logo_extracted && websiteBrandData.logo_url && (
                      <div className="mb-3 p-3 bg-green-900/30 border border-green-500/50 rounded-lg flex items-center gap-3">
                        <div className="bg-white rounded p-2">
                          <img 
                            src={`${BACKEND_URL}${websiteBrandData.logo_url}`}
                            alt="Extracted Logo"
                            className="h-12 w-auto object-contain"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="text-green-400 text-sm font-semibold">✅ Logo Extracted!</div>
                          <div className="text-xs text-gray-400">Will be used in AI images</div>
                        </div>
                      </div>
                    )}

                    {/* Color Preview */}
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      <div className="text-center">
                        <div 
                          className="h-16 rounded-lg border-2 border-gray-600 mb-1"
                          style={{ backgroundColor: websiteBrandData.primary }}
                        ></div>
                        <div className="text-xs text-gray-400">Primary</div>
                        <div className="text-xs text-gray-300 font-mono">{websiteBrandData.primary}</div>
                      </div>
                      <div className="text-center">
                        <div 
                          className="h-16 rounded-lg border-2 border-gray-600 mb-1"
                          style={{ backgroundColor: websiteBrandData.secondary }}
                        ></div>
                        <div className="text-xs text-gray-400">Secondary</div>
                        <div className="text-xs text-gray-300 font-mono">{websiteBrandData.secondary}</div>
                      </div>
                      <div className="text-center">
                        <div 
                          className="h-16 rounded-lg border-2 border-gray-600 mb-1"
                          style={{ backgroundColor: websiteBrandData.accent1 }}
                        ></div>
                        <div className="text-xs text-gray-400">Accent 1</div>
                        <div className="text-xs text-gray-300 font-mono">{websiteBrandData.accent1}</div>
                      </div>
                      <div className="text-center">
                        <div 
                          className="h-16 rounded-lg border-2 border-gray-600 mb-1"
                          style={{ backgroundColor: websiteBrandData.accent2 }}
                        ></div>
                        <div className="text-xs text-gray-400">Accent 2</div>
                        <div className="text-xs text-gray-300 font-mono">{websiteBrandData.accent2}</div>
                      </div>
                    </div>

                    <button
                      onClick={applyWebsiteBrand}
                      className="w-full px-4 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition"
                    >
                      ✅ Apply Brand Data
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Dual Logo Upload Section - Compact Row Layout */}
            <div className="space-y-3">
              <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-2.5">
                <p className="text-blue-200 text-xs">🌓 Upload logos for light and dark backgrounds. AI auto-selects based on image brightness.</p>
              </div>

              {/* Logos in One Row */}
              <div className="grid grid-cols-2 gap-3">
                {/* Logo for Light Backgrounds */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-300 mb-2">☀️ Light BG Logo</label>
                  
                  {logoLightPreview && (
                    <div className="mb-2 relative inline-block">
                      <div className="bg-white rounded-lg p-2 border border-gray-600">
                        <img 
                          src={logoLightPreview} 
                          alt="Logo Light" 
                          className="h-16 w-auto object-contain"
                        />
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/users/${user.id}/brand/logo?logo_type=light_bg`);
                            setLogoLightPreview(null);
                            setLogoLightFile(null);
                            alert('Logo deleted!');
                            onUserUpdate();
                          } catch (error) {
                            alert('Failed to delete');
                          }
                        }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  
                  <div className="space-y-1">
                    <label className="block">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              alert('Max 5MB');
                              return;
                            }
                            setLogoLightFile(file);
                            setLogoLightPreview(URL.createObjectURL(file));
                          }
                        }}
                        className="hidden"
                      />
                      <div className="px-2 py-1.5 bg-gray-800 border border-gray-700 text-gray-400 rounded text-center text-xs cursor-pointer hover:border-orange-500 transition">
                        {logoLightFile ? '📄 File selected' : '+ Upload'}
                      </div>
                    </label>
                    
                    {logoLightFile && (
                      <button
                        onClick={async () => {
                          setUploadingLogoLight(true);
                          try {
                            const formData = new FormData();
                            formData.append('file', logoLightFile);
                            
                            const response = await axios.post(
                              `${API}/users/${user.id}/brand/logo?logo_type=light_bg`,
                              formData,
                              { headers: { 'Content-Type': 'multipart/form-data' } }
                            );
                            
                            setLogoLightPreview(`${BACKEND_URL}${response.data.logo_url}`);
                            setLogoLightFile(null);
                            alert('Logo uploaded successfully!');
                            onUserUpdate();
                          } catch (error) {
                            console.error('Upload error:', error);
                            alert('Upload failed: ' + (error.response?.data?.detail || error.message));
                          } finally {
                            setUploadingLogoLight(false);
                          }
                        }}
                        disabled={uploadingLogoLight}
                        className="w-full px-2 py-1.5 bg-orange-500 text-white rounded text-xs hover:bg-orange-600 transition disabled:opacity-50"
                      >
                        {uploadingLogoLight ? '⏳' : '✓ Save'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Logo for Dark Backgrounds */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-300 mb-2">🌙 Dark BG Logo</label>
                  
                  {logoDarkPreview && (
                    <div className="mb-2 relative inline-block">
                      <div className="bg-gray-900 rounded-lg p-2 border border-gray-600">
                        <img 
                          src={logoDarkPreview} 
                          alt="Logo Dark" 
                          className="h-16 w-auto object-contain"
                        />
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/users/${user.id}/brand/logo?logo_type=dark_bg`);
                            setLogoDarkPreview(null);
                            setLogoDarkFile(null);
                            alert('Logo deleted!');
                            onUserUpdate();
                          } catch (error) {
                            alert('Failed to delete');
                          }
                        }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  
                  <div className="space-y-1">
                    <label className="block">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              alert('Max 5MB');
                              return;
                            }
                            setLogoDarkFile(file);
                            setLogoDarkPreview(URL.createObjectURL(file));
                          }
                        }}
                        className="hidden"
                      />
                      <div className="px-2 py-1.5 bg-gray-800 border border-gray-700 text-gray-400 rounded text-center text-xs cursor-pointer hover:border-orange-500 transition">
                        {logoDarkFile ? '📄 File selected' : '+ Upload'}
                      </div>
                    </label>
                    
                    {logoDarkFile && (
                      <button
                        onClick={async () => {
                          setUploadingLogoDark(true);
                          try {
                            const formData = new FormData();
                            formData.append('file', logoDarkFile);
                            
                            const response = await axios.post(
                              `${API}/users/${user.id}/brand/logo?logo_type=dark_bg`,
                              formData,
                              { headers: { 'Content-Type': 'multipart/form-data' } }
                            );
                            
                            setLogoDarkPreview(`${BACKEND_URL}${response.data.logo_url}`);
                            setLogoDarkFile(null);
                            alert('Logo uploaded successfully!');
                            onUserUpdate();
                          } catch (error) {
                            console.error('Upload error:', error);
                            alert('Upload failed: ' + (error.response?.data?.detail || error.message));
                          } finally {
                            setUploadingLogoDark(false);
                          }
                        }}
                        disabled={uploadingLogoDark}
                        className="w-full px-2 py-1.5 bg-orange-500 text-white rounded text-xs hover:bg-orange-600 transition disabled:opacity-50"
                      >
                        {uploadingLogoDark ? '⏳' : '✓ Save'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Brand Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Brand Name</label>
              <input
                type="text"
                value={brandSettings.brand_name}
                onChange={(e) => setBrandSettings({ ...brandSettings, brand_name: e.target.value })}
                placeholder="e.g., Your Company Name"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
              />
            </div>

            {/* Brand Colors - Grouped Layout */}
            <div className="space-y-4">
              {/* Core Colors (Mandatory) */}
              <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4">
                <h4 className="text-sm font-medium text-orange-200 mb-3">🎨 Core Brand Colors (Required)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-2">Primary Color *</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={brandSettings.brand_primary_color}
                        onChange={(e) => setBrandSettings({ ...brandSettings, brand_primary_color: e.target.value })}
                        className="h-10 w-14 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={brandSettings.brand_primary_color}
                        onChange={(e) => setBrandSettings({ ...brandSettings, brand_primary_color: e.target.value })}
                        placeholder="#FF6B35"
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-2">Secondary Color *</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={brandSettings.brand_secondary_color}
                        onChange={(e) => setBrandSettings({ ...brandSettings, brand_secondary_color: e.target.value })}
                        className="h-10 w-14 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={brandSettings.brand_secondary_color}
                        onChange={(e) => setBrandSettings({ ...brandSettings, brand_secondary_color: e.target.value })}
                        placeholder="#F7931E"
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Accent Colors (Optional) */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">✨ Accent Colors (Optional)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-2">Accent Color 1</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={brandSettings.brand_accent_color_1 || '#4ECDC4'}
                        onChange={(e) => setBrandSettings({ ...brandSettings, brand_accent_color_1: e.target.value })}
                        className="h-10 w-14 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={brandSettings.brand_accent_color_1 || ''}
                        onChange={(e) => setBrandSettings({ ...brandSettings, brand_accent_color_1: e.target.value })}
                        placeholder="#4ECDC4"
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-2">Accent Color 2</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={brandSettings.brand_accent_color_2 || '#95E1D3'}
                        onChange={(e) => setBrandSettings({ ...brandSettings, brand_accent_color_2: e.target.value })}
                        className="h-10 w-14 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={brandSettings.brand_accent_color_2 || ''}
                        onChange={(e) => setBrandSettings({ ...brandSettings, brand_accent_color_2: e.target.value })}
                        placeholder="#95E1D3"
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">💡 Add extra colors for richer AI-generated content</p>
              </div>
            </div>

            {/* AI Image Generation Controls */}
            <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-purple-200 font-medium">🤖 AI Image Generation Controls</h4>
              </div>
              <p className="text-xs text-purple-300 mb-4">Control how AI uses your branding in generated images</p>

              {/* Follow Brand Colors Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div>
                  <h5 className="text-white text-sm font-medium">Follow Brand Colors</h5>
                  <p className="text-xs text-gray-400">Use your brand colors in AI images</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={brandSettings.follow_brand_colors}
                    onChange={(e) => setBrandSettings({ ...brandSettings, follow_brand_colors: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                </label>
              </div>

              {/* Color Intensity Slider - Only show if follow_brand_colors is ON */}
              {brandSettings.follow_brand_colors && (
                <div className="p-3 bg-gray-800/50 rounded-lg">
                  <label className="block text-white text-sm font-medium mb-2">Color Intensity</label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="range"
                      min="0"
                      max="2"
                      value={brandSettings.color_intensity === 'low' ? 0 : brandSettings.color_intensity === 'medium' ? 1 : 2}
                      onChange={(e) => {
                        const intensity = ['low', 'medium', 'high'][parseInt(e.target.value)];
                        setBrandSettings({ ...brandSettings, color_intensity: intensity });
                      }}
                      className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <span className="text-sm text-purple-400 font-medium min-w-[60px]">
                      {brandSettings.color_intensity.charAt(0).toUpperCase() + brandSettings.color_intensity.slice(1)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Subtle</span>
                    <span>Balanced</span>
                    <span>Vibrant</span>
                  </div>
                </div>
              )}

              {/* Brand Text in AI Images Toggle */}
              <div className="p-4 bg-gray-800/50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <h5 className="text-white text-sm font-medium">📝 Brand Text in AI Images</h5>
                    <p className="text-xs text-gray-400 mt-1">Your brand name will appear as text in AI-generated images</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={brandSettings.brand_name_in_scene}
                      onChange={(e) => setBrandSettings({ ...brandSettings, brand_name_in_scene: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>
                
                {brandSettings.brand_name_in_scene && (
                  <div className="mt-3 p-3 bg-blue-900/10 border border-blue-500/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 text-sm">ℹ️</span>
                      <p className="text-xs text-gray-400">
                        AI will incorporate your brand name naturally into generated images as text overlay or within the scene composition.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Educational Text Overlay Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div>
                  <h5 className="text-white text-sm font-medium">Educational Text Overlay</h5>
                  <p className="text-xs text-gray-400">Add tips/rules/facts text on images</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={brandSettings.educational_overlay}
                    onChange={(e) => setBrandSettings({ ...brandSettings, educational_overlay: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                </label>
              </div>
            </div>

            {/* Watermark Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-700">
              <div>
                <h4 className="text-white font-medium">Add Logo as Watermark</h4>
                <p className="text-sm text-gray-400">Overlay your logo on generated images</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={brandSettings.watermark_enabled}
                  onChange={(e) => setBrandSettings({ ...brandSettings, watermark_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>

            {/* Watermark Customization - Show only if watermark is enabled */}
            {brandSettings.watermark_enabled && (
              <div className="p-4 bg-gray-800 rounded-lg border border-gray-700 space-y-4">
                <h4 className="text-white font-medium mb-3">Watermark Customization</h4>

                {/* Adaptive Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-white text-sm font-medium">Adaptive Watermark</h5>
                    <p className="text-xs text-gray-400">Auto-adjusts for dark/light backgrounds</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={brandSettings.watermark_adaptive}
                      onChange={(e) => setBrandSettings({ ...brandSettings, watermark_adaptive: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>

                {/* Position Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Watermark Position</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'bottom-right', label: 'Bottom Right' },
                      { value: 'bottom-left', label: 'Bottom Left' },
                      { value: 'top-right', label: 'Top Right' },
                      { value: 'top-left', label: 'Top Left' }
                    ].map((pos) => (
                      <button
                        key={pos.value}
                        onClick={() => setBrandSettings({ ...brandSettings, watermark_position: pos.value })}
                        className={`px-3 py-2 rounded-lg border-2 transition text-sm ${
                          brandSettings.watermark_position === pos.value
                            ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                            : 'border-gray-700 bg-gray-700 text-gray-300 hover:border-orange-500/50'
                        }`}
                      >
                        {pos.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Size Selection - Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-300">Watermark Size</label>
                    <span className="text-orange-400 font-bold text-sm">{brandSettings.watermark_size}%</span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="50"
                    value={brandSettings.watermark_size}
                    onChange={(e) => setBrandSettings({ ...brandSettings, watermark_size: e.target.value })}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
                    style={{
                      background: `linear-gradient(to right, #F97316 0%, #F97316 ${(brandSettings.watermark_size - 5) / 0.45}%, #374151 ${(brandSettings.watermark_size - 5) / 0.45}%, #374151 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5% (Minimal)</span>
                    <span>50% (Maximum)</span>
                  </div>
                </div>

                <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-3 text-blue-200 text-xs">
                  💡 These settings apply to all automatically generated watermarks. You can still adjust per-image in AI Makeover.
                </div>
              </div>
            )}

            {/* Preview */}
            <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
              <h4 className="text-white font-medium mb-3">Brand Preview</h4>
              <div 
                className="h-32 rounded-lg flex items-center justify-center text-white font-bold text-2xl"
                style={{
                  background: `linear-gradient(135deg, ${brandSettings.brand_primary_color}, ${brandSettings.brand_secondary_color})`
                }}
              >
                {brandSettings.brand_name || 'Your Brand Name'}
              </div>
            </div>

            {/* Success Message */}
            {brandSaveSuccess && (
              <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-lg text-sm flex items-center">
                <span className="text-xl mr-2">✅</span>
                <span>Brand settings saved successfully!</span>
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={async () => {
                setSavingBrand(true);
                setBrandSaveSuccess(false);
                try {
                  await axios.put(`${API}/users/${user.id}/brand`, brandSettings);
                  setBrandSaveSuccess(true);
                  onUserUpdate(); // Refresh user data
                  
                  // Hide success message after 3 seconds
                  setTimeout(() => {
                    setBrandSaveSuccess(false);
                  }, 3000);
                } catch (error) {
                  alert('Failed to save settings: ' + (error.response?.data?.detail || error.message));
                } finally {
                  setSavingBrand(false);
                }
              }}
              disabled={savingBrand}
              className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingBrand ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Saving...
                </span>
              ) : (
                '💾 Save Brand Settings'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Industries Tab */}
      {activeTab === 'industries' && (
        <div className="space-y-6">
          {/* Admin Industry Switcher */}
          {user.role === 'admin' && (
            <IndustrySwitcher user={user} onIndustryChange={onUserUpdate} compact={false} />
          )}

          {/* My Industries */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-orange-500/20 rounded-xl p-6">
            <h3 className="text-xl font-bold text-white mb-4">My Industries</h3>
            <p className="text-gray-400 text-sm mb-4">
              You have access to the following industries for content generation:
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              {user.subscribed_industries && user.subscribed_industries.map((industry) => (
                <div
                  key={industry}
                  className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between"
                >
                  <span className="text-green-400 text-sm font-medium">{industry}</span>
                  <span className="text-green-400">✓</span>
                </div>
              ))}
              {(!user.subscribed_industries || user.subscribed_industries.length === 0) && (
                <div className="col-span-full text-center py-8 text-gray-400">
                  No industries subscribed
                </div>
              )}
            </div>

            {user.role === 'admin' && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <p className="text-purple-400 text-sm">
                  🎉 <strong>Admin Access:</strong> You have full access to all {user.subscribed_industries?.length || 0} industries!
                </p>
              </div>
            )}
          </div>

          {/* Unlock More Industries (Only for regular users) */}
          {user.role !== 'admin' && (
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-orange-500/20 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Unlock More Industries</h3>
                <span className="px-4 py-1.5 bg-orange-500/20 text-orange-400 text-sm font-bold rounded-full">
                  $49 per industry
                </span>
              </div>
              
              <p className="text-gray-400 text-sm mb-6">
                Expand your content creation capabilities by unlocking additional industries.
              </p>

              {/* Available Industries */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-300 mb-3">Available Industries:</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    "Construction", "Medical", "Retail", "Technology", "Real Estate",
                    "Finance", "Education", "Food & Restaurant", "Fashion & Beauty",
                    "Travel & Hospitality", "Fitness & Wellness", "Legal Services",
                    "E-commerce", "Automotive", "Entertainment"
                  ].filter(ind => !user.subscribed_industries?.includes(ind)).map((industry) => (
                    <div
                      key={industry}
                      className="p-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-between group hover:border-orange-500/50 transition cursor-pointer"
                    >
                      <span className="text-gray-300 text-sm">{industry}</span>
                      <span className="text-gray-500 group-hover:text-orange-400 transition">🔒</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Unlock Button */}
              <button
                onClick={() => alert('Payment integration coming soon! Contact admin to unlock industries.')}
                className="w-full px-6 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-bold text-lg hover:from-orange-600 hover:to-amber-600 transition btn-glow"
              >
                🔓 Unlock Industries - $49 each
              </button>

              <p className="text-xs text-gray-500 text-center mt-3">
                * Payment integration with Stripe coming soon
              </p>
            </div>
          )}
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-orange-500/20 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-6">Notification Preferences</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
              <div>
                <h4 className="text-white font-medium">Email Notifications</h4>
                <p className="text-sm text-gray-400">Receive updates via email</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifications.emailNotifications}
                  onChange={(e) => setNotifications({ ...notifications, emailNotifications: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
              <div>
                <h4 className="text-white font-medium">Post Reminders</h4>
                <p className="text-sm text-gray-400">Get reminded to post regularly</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifications.postReminders}
                  onChange={(e) => setNotifications({ ...notifications, postReminders: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
              <div>
                <h4 className="text-white font-medium">Weekly Report</h4>
                <p className="text-sm text-gray-400">Receive weekly performance summary</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifications.weeklyReport}
                  onChange={(e) => setNotifications({ ...notifications, weeklyReport: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>

            <button
              onClick={saveNotifications}
              className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition btn-glow"
            >
              💾 Save Preferences
            </button>
          </div>
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-orange-500/20 rounded-xl p-6">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">⚙️</div>
            <h3 className="text-xl font-bold text-white mb-2">Preferences</h3>
            <p className="text-gray-400">Advanced preferences coming soon!</p>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-orange-500/20 rounded-xl p-6">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔒</div>
            <h3 className="text-xl font-bold text-white mb-2">Security Settings</h3>
            <p className="text-gray-400">Password and security options coming soon!</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsSection;
